import { ReactNode, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { X, Loader2, CheckCircle2, AlertTriangle, Info, Copy, Check } from 'lucide-react'
import { clsx, fa, toEnDigits } from '../lib/format'
import { useToast } from '../store'

/* ---------------------------------------------------------------- کارت */
export function Card({ className, children, hover }: { className?: string; children: ReactNode; hover?: boolean }) {
  return <div className={clsx('card p-5', hover && 'card-hover', className)}>{children}</div>
}

export function SectionHead({ title, subtitle, action }: { title: string; subtitle?: string; action?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h2 className="section-title">{title}</h2>
        {subtitle && <p className="mt-1.5 text-sm text-slate-400">{subtitle}</p>}
      </div>
      {action}
    </div>
  )
}

/* ---------------------------------------------------------------- دکمه */
type BtnProps = {
  children: ReactNode
  variant?: 'primary' | 'ghost' | 'aqua' | 'up' | 'down'
  loading?: boolean
  className?: string
  type?: 'button' | 'submit'
  disabled?: boolean
  onClick?: () => void
}
export function Button({ children, variant = 'primary', loading, className, ...rest }: BtnProps) {
  const map = { primary: 'btn-primary', ghost: 'btn-ghost', aqua: 'btn-aqua', up: 'btn-up', down: 'btn-down' }
  return (
    <button className={clsx(map[variant], className)} disabled={rest.disabled || loading} {...rest}>
      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
      {children}
    </button>
  )
}

/* ---------------------------------------------------------------- ورودی */
export function Field({
  label, hint, error, children,
}: { label?: string; hint?: ReactNode; error?: string; children: ReactNode }) {
  return (
    <div className="mb-4">
      {label && <label className="label">{label}</label>}
      {children}
      {error ? (
        <p className="mt-1.5 text-xs text-down">{error}</p>
      ) : hint ? (
        <p className="mt-1.5 text-xs text-slate-500">{hint}</p>
      ) : null}
    </div>
  )
}

/** ورودی عددی با پشتیبانی از ارقام فارسی و نمایش جداکننده */
export function NumberInput({
  value, onChange, placeholder, suffix, disabled,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  suffix?: ReactNode
  disabled?: boolean
}) {
  return (
    <div className="relative">
      <input
        className="input num pl-20 text-left"
        dir="ltr"
        inputMode="decimal"
        disabled={disabled}
        value={value}
        placeholder={placeholder}
        onChange={(e) => {
          const v = toEnDigits(e.target.value).replace(/[^\d.]/g, '')
          if ((v.match(/\./g) || []).length > 1) return
          onChange(v)
        }}
      />
      {suffix && (
        <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-xs font-medium text-slate-400">
          {suffix}
        </span>
      )}
    </div>
  )
}

/* ---------------------------------------------------------------- تب */
export function Tabs({
  items, value, onChange, size = 'md', className,
}: {
  items: { key: string; label: ReactNode }[]
  value: string
  onChange: (k: string) => void
  size?: 'sm' | 'md'
  className?: string
}) {
  return (
    <div className={clsx('inline-flex flex-wrap gap-1 rounded-xl border border-white/[.07] bg-white/[.03] p-1', className)}>
      {items.map((it) => (
        <button
          key={it.key}
          onClick={() => onChange(it.key)}
          className={clsx(
            'rounded-lg font-medium transition-all duration-200',
            size === 'sm' ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm',
            value === it.key
              ? 'bg-gradient-to-l from-brand-600 to-brand-400 text-white shadow-glow'
              : 'text-slate-400 hover:bg-white/[.05] hover:text-slate-200',
          )}
        >
          {it.label}
        </button>
      ))}
    </div>
  )
}

/* ---------------------------------------------------------------- مودال */
export function Modal({
  open, onClose, title, children, wide,
}: { open: boolean; onClose: () => void; title: string; children: ReactNode; wide?: boolean }) {
  useEffect(() => {
    const fn = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    if (open) document.addEventListener('keydown', fn)
    document.body.style.overflow = open ? 'hidden' : ''
    return () => {
      document.removeEventListener('keydown', fn)
      document.body.style.overflow = ''
    }
  }, [open, onClose])

  if (!open) return null
  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-end justify-center bg-ink-950/80 p-0 backdrop-blur-md sm:items-center sm:p-4">
      <div className="absolute inset-0" onClick={onClose} />
      <div
        className={clsx(
          'relative max-h-[92vh] w-full overflow-y-auto rounded-t-3xl border border-white/10 bg-ink-900/95 p-4 shadow-card sm:rounded-3xl sm:p-6',
          wide ? 'sm:max-w-3xl' : 'sm:max-w-md',
        )}
        // روی آیفون، دکمه پایین پنجره زیر نوار خانه گیر می‌کرد
        style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}
      >
        <div className="mb-5 flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">{title}</h3>
          <button onClick={onClose} aria-label="بستن"
            className="tap rounded-lg p-2 text-slate-400 transition hover:bg-white/10 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  )
}

/* ---------------------------------------------------------------- نشان */
export function Badge({ kind = 'info', children }: { kind?: 'up' | 'down' | 'info' | 'gold' | 'muted'; children: ReactNode }) {
  const map = {
    up: 'bg-up/12 text-up border-up/25',
    down: 'bg-down/12 text-down border-down/25',
    info: 'bg-brand-500/12 text-brand-200 border-brand-500/25',
    gold: 'bg-gold/12 text-gold border-gold/25',
    muted: 'bg-white/[.05] text-slate-400 border-white/10',
  }
  return <span className={clsx('inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium', map[kind])}>{children}</span>
}

export const statusBadge = (s: string) => {
  const map: Record<string, [any, string]> = {
    open: ['info', 'باز'], partial: ['gold', 'پر شده جزئی'], filled: ['up', 'تکمیل شد'],
    canceled: ['muted', 'لغو شد'], pending: ['gold', 'در انتظار'], confirmed: ['up', 'موفق'],
    rejected: ['down', 'رد شد'], active: ['up', 'فعال'], finished: ['muted', 'پایان‌یافته'],
    approved: ['up', 'تأیید شده'], none: ['muted', 'انجام نشده'],
  }
  const [kind, label] = map[s] ?? ['muted', s]
  return <Badge kind={kind}>{label}</Badge>
}

/* ---------------------------------------------------------------- کپی */
export function CopyButton({ text, label }: { text: string; label?: string }) {
  const [done, setDone] = useState(false)
  return (
    <button
      onClick={() => {
        navigator.clipboard?.writeText(text)
        setDone(true)
        setTimeout(() => setDone(false), 1600)
      }}
      className="btn-ghost !px-3 !py-2 text-xs"
    >
      {done ? <Check className="h-4 w-4 text-up" /> : <Copy className="h-4 w-4" />}
      {label ?? (done ? 'کپی شد' : 'کپی')}
    </button>
  )
}

/* ---------------------------------------------------------------- خالی/لودینگ */
export function Empty({ text, icon }: { text: string; icon?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-14 text-center">
      <div className="rounded-2xl border border-white/10 bg-white/[.03] p-4 text-slate-500">{icon ?? <Info className="h-6 w-6" />}</div>
      <p className="text-sm text-slate-500">{text}</p>
    </div>
  )
}

export function Rows({ n = 6, h = 'h-12' }: { n?: number; h?: string }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: n }).map((_, i) => (
        <div key={i} className={clsx('skeleton w-full', h)} />
      ))}
    </div>
  )
}

/* ---------------------------------------------------------------- توست */
export function Toaster() {
  const items = useToast((s) => s.items)
  const remove = useToast((s) => s.remove)
  const icon = { success: CheckCircle2, error: AlertTriangle, info: Info }
  const color = { success: 'text-up border-up/30', error: 'text-down border-down/30', info: 'text-brand-200 border-brand-500/30' }
  return createPortal(
    <div className="pointer-events-none fixed bottom-5 left-1/2 z-[200] flex w-[min(94vw,26rem)] -translate-x-1/2 flex-col gap-2">
      {items.map((t) => {
        const Icon = icon[t.kind]
        return (
          <div
            key={t.id}
            onClick={() => remove(t.id)}
            className={clsx(
              'pointer-events-auto flex cursor-pointer items-center gap-3 rounded-2xl border bg-ink-900/95 px-4 py-3 text-sm shadow-card backdrop-blur-xl',
              color[t.kind],
            )}
          >
            <Icon className="h-5 w-5 shrink-0" />
            <span className="text-slate-200">{t.text}</span>
          </div>
        )
      })}
    </div>,
    document.body,
  )
}

/* ---------------------------------------------------------------- شمارنده انیمیشنی */
export function Counter({ value, decimals = 0 }: { value: number; decimals?: number }) {
  const [shown, setShown] = useState(0)
  const raf = useRef<number>()
  useEffect(() => {
    const from = shown
    const start = performance.now()
    const step = (t: number) => {
      const p = Math.min((t - start) / 900, 1)
      setShown(from + (value - from) * (1 - Math.pow(1 - p, 3)))
      if (p < 1) raf.current = requestAnimationFrame(step)
    }
    raf.current = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf.current!)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])
  return <>{fa(shown.toLocaleString('en-US', { maximumFractionDigits: decimals }))}</>
}
